import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ChevronLeft, ChevronRight, Check, Plus, Trash2, Package, FileText, IndianRupee, MapPin, UserCheck, Save } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const h = React.createElement;

const STEPS = [
  { id: 0, title: "Type & Funding", icon: Package, desc: "Entry type and source of funds" },
  { id: 1, title: "Procurement", icon: FileText, desc: "PO, vendor, and indent details" },
  { id: 2, title: "Items & Pricing", icon: IndianRupee, desc: "Item details and cost breakdown" },
  { id: 3, title: "Asset & Location", icon: MapPin, desc: "Asset register and location" },
  { id: 4, title: "Verification", icon: UserCheck, desc: "Verifiers, project, and remarks" },
];
const CLASSIFICATIONS = ["Electronics", "Furniture", "Machinery", "Instruments", "IT Equipment", "Lab Equipment", "Chemicals", "Glassware", "Stationery", "Others"];
const ACCOUNT_CATEGORIES = ["Capital Expenditure", "Revenue Expenditure", "Project Expenditure", "Grant Expenditure"];
const emptyItem = () => ({ serial_no: 1, description: "", hsn_code: "", classification: "", asset_category: "", subcategory: "", make: "", model: "", item_serial_number: "", quantity: 1, unit: "Nos", basic_price: 0, gst_charges: 0, customs_charges: 0, packing_forwarding: 0, transport_installation: 0 });

function GRNForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEdit = !!editId;
  const { user, getAuthHeader } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [poList, setPoList] = useState([]);
  const [poLoading, setPoLoading] = useState(false);
  const [assetCategories, setAssetCategories] = useState([]); // [{name, subcategories:[...]}]
  const [form, setForm] = useState({
    entry_type: "ASSET", grn_source: "DIRECT", po_reference_id: "",
    source_of_fund: "INSTITUTE", project_number: "", fund_category: "",
    category_as_per_accounts: "", financial_year: "", indent_reference_no: "",
    indentor_name: user?.name || "", indentor_department: user?.department || "",
    purchase_order_no: "", purchase_order_year: new Date().getFullYear().toString(),
    purchase_order_date: "", vendor_name: "", date_of_receipt: "", invoice_amount: 0,
    invoice_no: "", invoice_date: "", items: [emptyItem()],
    accounting_voucher_no: "", ledger_book_no: "", asset_entry_date: "", pir_in_name_of: "",
    asset_holder_name: "", asset_department: user?.department || "", asset_location: "",
    stores_remarks: "", asset_verifier_name: "", physical_verifier_remarks: "",
    stores_section_remarks: "", project_name: "", principal_investigator: "",
    barcode: "", remarks: "",
  });
  const [errors, setErrors] = useState({});

  const fetchPOs = useCallback(async () => {
    setPoLoading(true);
    try {
      const r = await axios.get(API_URL + "/purchase-orders-for-grn", getAuthHeader());
      setPoList(r.data);
    } catch {}
    finally { setPoLoading(false); }
  }, []);

  // Load Asset Category master (Categories.xlsx sourced on the backend)
  useEffect(() => {
    axios.get(API_URL + "/categories", getAuthHeader())
      .then(r => setAssetCategories((r.data && r.data.categories) || []))
      .catch(() => { /* non-blocking — dropdown will be empty if fetch fails */ });
  }, []);

  // Edit hydration
  useEffect(() => {
    if (!isEdit) return;
    axios.get(API_URL + "/grn/" + editId, getAuthHeader())
      .then(r => {
        const grn = r.data;
        setForm(f => ({ ...f, ...grn,
          items: (grn.items && grn.items.length) ? grn.items : f.items
        }));
      })
      .catch(() => toast.error("Failed to load GRN for editing"));
  }, [editId]);

  const handlePOSelect = useCallback((poId) => {
    const po = poList.find(p => p.id === poId);
    if (!po) return;
    // Map PO items (prefer PO's own items, fallback to requisition items)
    const sourceItems = po.items?.length ? po.items : (po.requisition_items || []);
    const poItems = sourceItems.map((item, idx) => ({
      serial_no: idx + 1,
      description: item.description || "",
      hsn_code: item.hsn_code || "",
      classification: item.classification || "",
      asset_category: item.asset_category || "",
      subcategory: item.subcategory || "",
      make: item.make || item.make_model || "",
      model: item.model || "",
      item_serial_number: item.item_serial_number || "",
      quantity: item.quantity || 1,
      unit: item.unit || "Nos",
      basic_price: item.unit_price || item.estimated_cost || item.basic_price || 0,
      gst_charges: item.gst_charges || 0,
      customs_charges: item.customs_charges || 0,
      packing_forwarding: item.packing_forwarding || 0,
      transport_installation: item.transport_installation || 0,
    }));
    setForm(f => ({
      ...f,
      po_reference_id: poId,
      purchase_order_no: po.po_number || "",
      purchase_order_date: po.po_date ? po.po_date.slice(0, 10) : "",
      purchase_order_year: po.po_date ? new Date(po.po_date).getFullYear().toString() : f.purchase_order_year,
      vendor_name: po.vendor_details?.name || "",
      source_of_fund: po.fund_type || f.source_of_fund,
      indentor_name: po.indentor_name || f.indentor_name,
      indentor_department: po.department || f.indentor_department,
      invoice_amount: po.po_value || 0,
      items: poItems.length > 0 ? poItems : f.items,
    }));
    setErrors({});
  }, [poList]);

  const set = useCallback((key, val) => setForm(f => ({ ...f, [key]: val })), []);
  const setItem = useCallback((idx, key, val) => {
    setForm(f => { const items = [...f.items]; items[idx] = { ...items[idx], [key]: val }; return { ...f, items }; });
  }, []);
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem(), serial_no: f.items.length + 1 }] }));
  const removeItem = (idx) => { if (form.items.length <= 1) return; setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx).map((item, i) => ({ ...item, serial_no: i + 1 })) })); };
  const calcItemTotal = (item) => ((item.basic_price || 0) + (item.gst_charges || 0) + (item.customs_charges || 0) + (item.packing_forwarding || 0) + (item.transport_installation || 0)) * (item.quantity || 1);
  const grandTotal = form.items.reduce((sum, item) => sum + calcItemTotal(item), 0);

  const handleSubmit = async () => {
    const errs = {};
    if (!form.date_of_receipt) errs.date_of_receipt = "Date of Receipt is required";
    if (!form.items.length || !form.items[0].description) errs.items = "At least one item with a description is required";
    if (form.grn_source === "PO_BASED" && !form.po_reference_id) errs.po_reference_id = "Select a Purchase Order";
    if (!form.source_of_fund) errs.source_of_fund = "Source of fund is required";
    if (grandTotal <= 0) errs.total = "Total value must be greater than zero";
    if (Object.keys(errs).length > 0) { setErrors(errs); Object.values(errs).forEach(e => toast.error(e)); return; }
    setErrors({});
    setSubmitting(true);
    try {
      if (isEdit) {
        await axios.put(API_URL + "/grn/" + editId, { ...form, invoice_amount: grandTotal }, getAuthHeader());
        toast.success("GRN entry updated successfully!");
      } else {
        await axios.post(API_URL + "/grn", { ...form, invoice_amount: grandTotal }, getAuthHeader());
        toast.success("GRN entry created successfully!");
      }
      navigate("/grn");
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) msg = detail.map(d => `${Array.isArray(d.loc) ? d.loc.slice(1).join(".") : "field"}: ${d.msg}`).join("; ");
      else msg = (typeof detail === "string" ? detail : null) || "Failed to create GRN";
      toast.error(msg);
    }
    finally { setSubmitting(false); }
  };

  const canNext = () => {
    if (step === 0) return form.entry_type && form.source_of_fund;
    if (step === 1) return form.date_of_receipt;
    if (step === 2) return form.items.length > 0 && form.items[0].description;
    return true;
  };

  const renderField = (label, content, required, span) => {
    return h("div", { className: span === 2 ? "md:col-span-2" : "" },
      h(Label, { className: "text-xs font-medium text-slate-600" }, label, required && h("span", { className: "text-rose-500 ml-0.5" }, "*")),
      h("div", { className: "mt-1.5" }, content)
    );
  };

  const renderSelect = (value, onChange, options, placeholder) => {
    return h("select", { value, onChange: e => onChange(e.target.value), className: "w-full h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" },
      placeholder && h("option", { value: "" }, placeholder),
      options.map(o => h("option", { key: typeof o === "string" ? o : o.value, value: typeof o === "string" ? o : o.value }, typeof o === "string" ? o : o.label))
    );
  };

  const renderStepper = () => {
    return h("div", { className: "flex items-center gap-1 p-1 bg-slate-50 rounded-xl border border-slate-100" },
      STEPS.map((s, i) => {
        const isActive = step === i, isDone = step > i;
        return h("button", { key: s.id, onClick: () => setStep(i),
          className: "flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-all duration-200 " + (isActive ? "bg-white shadow-sm border border-slate-200" : isDone ? "opacity-80 hover:opacity-100" : "opacity-50")
        },
          h("div", { className: "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 " + (isDone ? "bg-emerald-500 text-white" : isActive ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500") },
            isDone ? h(Check, { className: "w-3.5 h-3.5" }) : i + 1
          ),
          h("div", { className: "hidden lg:block min-w-0" },
            h("p", { className: "text-xs font-semibold truncate " + (isActive ? "text-slate-900" : "text-slate-500") }, s.title)
          )
        );
      })
    );
  };

  const renderStep0 = () => {
    return h("div", { className: "space-y-6" },
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Entry Type"),
        h("div", { className: "flex gap-3" },
          [{ v: "ASSET", l: "Asset", d: "Non-consumable items entered in asset register" }, { v: "CONSUMABLE", l: "Consumable", d: "Items consumed during use" }].map(opt =>
            h("button", { key: opt.v, onClick: () => set("entry_type", opt.v), "data-testid": "type-" + opt.v.toLowerCase(),
              className: "flex-1 p-4 rounded-xl border-2 text-left transition-all " + (form.entry_type === opt.v ? "border-slate-900 bg-slate-50 shadow-sm" : "border-slate-200 hover:border-slate-300")
            },
              h("p", { className: "font-semibold text-sm " + (form.entry_type === opt.v ? "text-slate-900" : "text-slate-600") }, opt.l),
              h("p", { className: "text-xs text-slate-400 mt-1" }, opt.d)
            )
          )
        )
      ),
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "GRN Source"),
        h("div", { className: "flex gap-3" },
          [{ v: "DIRECT", l: "Direct Entry", d: "Goods received without a Purchase Order" }, { v: "PO_BASED", l: "Based on Purchase Order", d: "Link to existing PO \u2014 auto-fills details" }].map(opt =>
            h("button", { key: opt.v, onClick: () => { set("grn_source", opt.v); if (opt.v === "PO_BASED" && poList.length === 0) fetchPOs(); }, "data-testid": "source-" + opt.v.toLowerCase(),
              className: "flex-1 p-4 rounded-xl border-2 text-left transition-all " + (form.grn_source === opt.v ? "border-blue-600 bg-blue-50 shadow-sm" : "border-slate-200 hover:border-slate-300")
            },
              h("p", { className: "font-semibold text-sm " + (form.grn_source === opt.v ? "text-blue-900" : "text-slate-600") }, opt.l),
              h("p", { className: "text-xs text-slate-400 mt-1" }, opt.d)
            )
          )
        ),
        form.grn_source === "PO_BASED" && h("div", { className: "mt-3 p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-3" },
          h("label", { className: "text-xs font-semibold text-blue-800 uppercase tracking-wider" }, "Select Purchase Order *"),
          poLoading ? h("p", { className: "text-sm text-blue-600" }, "Loading POs...") :
          h("select", { value: form.po_reference_id, onChange: e => handlePOSelect(e.target.value), "data-testid": "po-select",
            className: "w-full h-10 px-3 rounded-md border text-sm bg-white " + (errors.po_reference_id ? "border-rose-400" : "border-blue-300")
          },
            h("option", { value: "" }, "-- Select a Purchase Order --"),
            poList.map(po => h("option", { key: po.id, value: po.id },
              po.po_number + " \u2014 " + (po.vendor_details?.name || "N/A") + " \u2014 \u20B9" + (po.po_value || 0).toLocaleString("en-IN")
            ))
          ),
          errors.po_reference_id && h("p", { className: "text-xs text-rose-600" }, errors.po_reference_id),
          form.po_reference_id && h("div", { className: "p-3 bg-white rounded-lg border border-blue-100 text-sm space-y-1" },
            h("p", { className: "font-medium text-slate-900" }, "PO: ", form.purchase_order_no),
            h("p", { className: "text-slate-600" }, "Vendor: ", form.vendor_name),
            h("p", { className: "text-slate-600" }, "Items pre-filled: ", form.items.length)
          )
        )
      ),
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Funding Details"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" },
          renderField("Source of Fund", renderSelect(form.source_of_fund, v => set("source_of_fund", v), [{ value: "INSTITUTE", label: "Institute Funds" }, { value: "PROJECT", label: "Project Funds" }]), true),
          renderField("Project No. / Fund Category", h(Input, { value: form.project_number, onChange: e => set("project_number", e.target.value), placeholder: "e.g. CSE/SERB/2024/001" })),
          renderField("Category as per Accounts", renderSelect(form.category_as_per_accounts, v => set("category_as_per_accounts", v), ACCOUNT_CATEGORIES, "Select...")),
          renderField("Financial Year", h(Input, { value: form.financial_year, onChange: e => set("financial_year", e.target.value), placeholder: "2025-26" })),
          renderField("Fund Category", h(Input, { value: form.fund_category, onChange: e => set("fund_category", e.target.value), placeholder: "General / Laboratory / IT" }))
        )
      )
    );
  };

  const renderStep1 = () => {
    return h("div", { className: "space-y-6" },
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Indent Information"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" },
          renderField("Indent Reference No.", h(Input, { value: form.indent_reference_no, onChange: e => set("indent_reference_no", e.target.value), placeholder: "IND/2025/00012" })),
          renderField("Indentor Name", h(Input, { value: form.indentor_name, onChange: e => set("indentor_name", e.target.value) })),
          renderField("Indentor Department", h(Input, { value: form.indentor_department, onChange: e => set("indentor_department", e.target.value) }))
        )
      ),
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Purchase Order"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" },
          renderField("Purchase Order No.", h(Input, { value: form.purchase_order_no, onChange: e => set("purchase_order_no", e.target.value), placeholder: "PO/2025/00045", "data-testid": "po-no" })),
          renderField("PO Year", h(Input, { value: form.purchase_order_year, onChange: e => set("purchase_order_year", e.target.value) })),
          renderField("PO Date", h(Input, { type: "date", value: form.purchase_order_date, onChange: e => set("purchase_order_date", e.target.value) }))
        )
      ),
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Vendor & Invoice"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" },
          renderField("Name of the Firm", h(Input, { value: form.vendor_name, onChange: e => set("vendor_name", e.target.value), placeholder: "Vendor name", "data-testid": "vendor-name" }), false, 2),
          renderField("Date of Receipt", h(Input, { type: "date", value: form.date_of_receipt, onChange: e => set("date_of_receipt", e.target.value), "data-testid": "receipt-date" }), true),
          renderField("Invoice No.", h(Input, { value: form.invoice_no, onChange: e => set("invoice_no", e.target.value) })),
          renderField("Invoice Date", h(Input, { type: "date", value: form.invoice_date, onChange: e => set("invoice_date", e.target.value) })),
          renderField("Invoice Amount", h(Input, { type: "number", value: form.invoice_amount || "", onChange: e => set("invoice_amount", parseFloat(e.target.value) || 0) }))
        )
      )
    );
  };

  // Compact in-cell input/select helpers for the items table
  const cellInput = (props) => h(Input, { ...props, className: "text-sm h-8 border-slate-200 px-2 " + (props.className || "") });
  const cellSelect = (value, onChange, options, testid) => h("select", {
    value, onChange: e => onChange(e.target.value),
    className: "h-8 px-2 rounded border border-slate-200 text-xs bg-white w-full",
    "data-testid": testid
  }, options.map(o => {
    const val = typeof o === "string" ? o : o.value;
    const lbl = typeof o === "string" ? o : o.label;
    return h("option", { key: val, value: val }, lbl);
  }));

  // ========== STEP 2: Items (Table format - mirrors PO design) ==========
  const renderStep2 = () => {
    const totalsByCol = form.items.reduce((acc, it) => {
      const q = it.quantity || 1;
      acc.basic += (it.basic_price || 0) * q;
      acc.gst += (it.gst_charges || 0) * q;
      acc.customs += (it.customs_charges || 0) * q;
      acc.pf += (it.packing_forwarding || 0) * q;
      acc.transport += (it.transport_installation || 0) * q;
      return acc;
    }, { basic: 0, gst: 0, customs: 0, pf: 0, transport: 0 });

    return h("div", { className: "space-y-4" },
      h("div", { className: "flex items-center justify-between" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider" }, "Items Received"),
        h(Button, { variant: "outline", size: "sm", onClick: addItem, "data-testid": "add-item-btn" }, h(Plus, { className: "w-3.5 h-3.5 mr-1" }), "Add Item")
      ),
      // Items Table — wide, horizontally scrollable on small screens
      h("div", { className: "border rounded-xl overflow-x-auto" },
        h(Table, { className: "min-w-[1600px]" },
          h(TableHeader, null, h(TableRow, { className: "bg-slate-900 hover:bg-slate-900" },
            h(TableHead, { className: "text-white font-semibold w-10" }, "#"),
            h(TableHead, { className: "text-white font-semibold min-w-[200px]" }, "Description"),
            h(TableHead, { className: "text-white font-semibold w-24" }, "HSN"),
            h(TableHead, { className: "text-white font-semibold w-44" }, "Asset Category"),
            h(TableHead, { className: "text-white font-semibold w-40" }, "Subcategory"),
            h(TableHead, { className: "text-white font-semibold w-28" }, "Make"),
            h(TableHead, { className: "text-white font-semibold w-28" }, "Model"),
            h(TableHead, { className: "text-white font-semibold w-28" }, "Item S/N"),
            h(TableHead, { className: "text-white font-semibold w-16 text-center" }, "Qty"),
            h(TableHead, { className: "text-white font-semibold w-20" }, "Unit"),
            h(TableHead, { className: "text-white font-semibold w-28 text-right" }, "Basic \u20B9"),
            h(TableHead, { className: "text-white font-semibold w-24 text-right" }, "GST \u20B9"),
            h(TableHead, { className: "text-white font-semibold w-24 text-right" }, "Customs \u20B9"),
            h(TableHead, { className: "text-white font-semibold w-24 text-right" }, "P&F \u20B9"),
            h(TableHead, { className: "text-white font-semibold w-24 text-right" }, "Transport \u20B9"),
            h(TableHead, { className: "text-white font-semibold w-28 text-right" }, "Total"),
            h(TableHead, { className: "text-white w-10" })
          )),
          h(TableBody, null,
            form.items.map((item, idx) => h(TableRow, { key: idx, className: idx % 2 === 0 ? "bg-white" : "bg-slate-50/50", "data-testid": "item-row-" + idx },
              h(TableCell, { className: "font-mono text-xs text-slate-500 text-center" }, item.serial_no),
              h(TableCell, null, cellInput({ value: item.description, onChange: e => setItem(idx, "description", e.target.value), placeholder: "Item description", "data-testid": "item-desc-" + idx })),
              h(TableCell, null, cellInput({ value: item.hsn_code, onChange: e => setItem(idx, "hsn_code", e.target.value), placeholder: "HSN" })),
              h(TableCell, null, cellSelect(
                item.asset_category,
                v => { setItem(idx, "asset_category", v); setItem(idx, "subcategory", ""); },
                [{ value: "", label: "Select category..." }, ...assetCategories.map(c => ({ value: c.name, label: c.name }))],
                "item-asset-category-" + idx
              )),
              h(TableCell, null, cellSelect(
                item.subcategory,
                v => setItem(idx, "subcategory", v),
                [
                  { value: "", label: item.asset_category ? "Select subcategory..." : "\u2014 Pick category first \u2014" },
                  ...((assetCategories.find(c => c.name === item.asset_category) || {}).subcategories || []).map(s => ({ value: s, label: s }))
                ],
                "item-subcategory-" + idx
              )),
              h(TableCell, null, cellInput({ value: item.make, onChange: e => setItem(idx, "make", e.target.value), placeholder: "Brand" })),
              h(TableCell, null, cellInput({ value: item.model, onChange: e => setItem(idx, "model", e.target.value), placeholder: "Model" })),
              h(TableCell, null, cellInput({ value: item.item_serial_number, onChange: e => setItem(idx, "item_serial_number", e.target.value), placeholder: "Serial #" })),
              h(TableCell, null, cellInput({ type: "number", min: 1, value: item.quantity, onChange: e => setItem(idx, "quantity", parseInt(e.target.value) || 1), className: "text-center" })),
              h(TableCell, null, cellSelect(item.unit, v => setItem(idx, "unit", v), ["Nos", "Kg", "Ltr", "Mtr", "Set", "Box", "Pkt"])),
              h(TableCell, { className: "text-right" }, cellInput({ type: "number", value: item.basic_price || "", onChange: e => setItem(idx, "basic_price", parseFloat(e.target.value) || 0), className: "text-right", "data-testid": "basic-price-" + idx })),
              h(TableCell, { className: "text-right" }, cellInput({ type: "number", value: item.gst_charges || "", onChange: e => setItem(idx, "gst_charges", parseFloat(e.target.value) || 0), className: "text-right" })),
              h(TableCell, { className: "text-right" }, cellInput({ type: "number", value: item.customs_charges || "", onChange: e => setItem(idx, "customs_charges", parseFloat(e.target.value) || 0), className: "text-right" })),
              h(TableCell, { className: "text-right" }, cellInput({ type: "number", value: item.packing_forwarding || "", onChange: e => setItem(idx, "packing_forwarding", parseFloat(e.target.value) || 0), className: "text-right" })),
              h(TableCell, { className: "text-right" }, cellInput({ type: "number", value: item.transport_installation || "", onChange: e => setItem(idx, "transport_installation", parseFloat(e.target.value) || 0), className: "text-right" })),
              h(TableCell, { className: "text-right font-semibold text-emerald-700 text-sm whitespace-nowrap", "data-testid": "item-total-" + idx }, "\u20B9" + calcItemTotal(item).toLocaleString("en-IN")),
              h(TableCell, null, form.items.length > 1 && h("button", { onClick: () => removeItem(idx), className: "p-1 rounded hover:bg-rose-50 text-rose-400 hover:text-rose-600", "data-testid": "remove-item-" + idx }, h(Trash2, { className: "w-3.5 h-3.5" })))
            )),
            // Totals row
            h(TableRow, { className: "border-t-2 border-slate-200 bg-slate-50 hover:bg-slate-50 font-medium" },
              h(TableCell, { colSpan: 10, className: "text-right text-xs uppercase tracking-wider text-slate-600" }, "Sub-Totals"),
              h(TableCell, { className: "text-right text-sm text-slate-700" }, "\u20B9" + totalsByCol.basic.toLocaleString("en-IN")),
              h(TableCell, { className: "text-right text-sm text-slate-700" }, "\u20B9" + totalsByCol.gst.toLocaleString("en-IN")),
              h(TableCell, { className: "text-right text-sm text-slate-700" }, "\u20B9" + totalsByCol.customs.toLocaleString("en-IN")),
              h(TableCell, { className: "text-right text-sm text-slate-700" }, "\u20B9" + totalsByCol.pf.toLocaleString("en-IN")),
              h(TableCell, { className: "text-right text-sm text-slate-700" }, "\u20B9" + totalsByCol.transport.toLocaleString("en-IN")),
              h(TableCell, { className: "text-right text-sm font-bold text-emerald-700" }, "\u20B9" + grandTotal.toLocaleString("en-IN")),
              h(TableCell, null)
            )
          )
        )
      ),
      // Helper hint
      h("p", { className: "text-xs text-slate-400 px-1" }, "Tip: Item Total = (Basic + GST + Customs + P&F + Transport) \u00D7 Qty. Scroll horizontally if needed."),
      // Grand total bar
      h("div", { className: "flex justify-end p-4 bg-slate-900 rounded-xl text-white" },
        h("div", { className: "text-right space-y-0.5" },
          h("div", { className: "flex justify-between gap-8 text-xs text-slate-400" }, h("span", null, "Items"), h("span", null, form.items.length)),
          h("div", { className: "flex justify-between gap-8 pt-2 border-t border-slate-700 mt-1" },
            h("span", { className: "text-sm font-medium" }, "GRAND TOTAL"),
            h("span", { className: "text-2xl font-bold", "data-testid": "grand-total" }, "\u20B9" + grandTotal.toLocaleString("en-IN"))
          )
        )
      )
    );
  };

  const renderStep3 = () => {
    if (form.entry_type !== "ASSET") {
      return h("div", { className: "text-center py-12 text-slate-400" },
        h(Package, { className: "w-12 h-12 mx-auto mb-3 opacity-40" }),
        h("p", { className: "font-medium text-slate-600" }, "Consumable Entry"),
        h("p", { className: "text-sm mt-1" }, "Asset register fields are not applicable for consumables.")
      );
    }
    return h("div", { className: "space-y-6" },
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Asset Register"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" },
          renderField("Accounting Voucher No.", h(Input, { value: form.accounting_voucher_no, onChange: e => set("accounting_voucher_no", e.target.value) })),
          renderField("Ledger/Book No. & Issue Slip", h(Input, { value: form.ledger_book_no, onChange: e => set("ledger_book_no", e.target.value) })),
          renderField("Asset Entry Date", h(Input, { type: "date", value: form.asset_entry_date, onChange: e => set("asset_entry_date", e.target.value) }))
        )
      ),
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Asset Holder & Location"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" },
          renderField("PIR In Name Of", h(Input, { value: form.pir_in_name_of, onChange: e => set("pir_in_name_of", e.target.value) })),
          renderField("Asset Holder / Controller", h(Input, { value: form.asset_holder_name, onChange: e => set("asset_holder_name", e.target.value), "data-testid": "asset-holder" })),
          renderField("Department", h(Input, { value: form.asset_department, onChange: e => set("asset_department", e.target.value) })),
          renderField("Location", h(Input, { value: form.asset_location, onChange: e => set("asset_location", e.target.value), placeholder: "Room, Building, Floor" })),
          renderField("Barcode", h(Input, { value: form.barcode, onChange: e => set("barcode", e.target.value), placeholder: "Scan or enter barcode", "data-testid": "barcode" }))
        )
      ),
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Stores"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
          renderField("Stores Remarks", h(Textarea, { value: form.stores_remarks, onChange: e => set("stores_remarks", e.target.value), rows: 2 }), false, 2)
        )
      )
    );
  };

  const renderStep4 = () => {
    return h("div", { className: "space-y-6" },
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Verification"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
          renderField("Name of the Asset Verifier", h(Input, { value: form.asset_verifier_name, onChange: e => set("asset_verifier_name", e.target.value) })),
          renderField("Physical Verifier Remarks", h(Textarea, { value: form.physical_verifier_remarks, onChange: e => set("physical_verifier_remarks", e.target.value), rows: 2 }))
        )
      ),
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Stores Section"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
          renderField("Stores Section Remarks", h(Textarea, { value: form.stores_section_remarks, onChange: e => set("stores_section_remarks", e.target.value), rows: 2 }), false, 2)
        )
      ),
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Project Details"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
          renderField("Name of the Project", h(Input, { value: form.project_name, onChange: e => set("project_name", e.target.value) })),
          renderField("Principal Investigator", h(Input, { value: form.principal_investigator, onChange: e => set("principal_investigator", e.target.value) }))
        )
      ),
      h("div", { className: "space-y-3" },
        h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Additional"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
          renderField("General Remarks", h(Textarea, { value: form.remarks, onChange: e => set("remarks", e.target.value), rows: 2, placeholder: "Any additional remarks..." }), false, 2)
        )
      ),
      h("div", { className: "p-5 bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl text-white space-y-3" },
        h("h3", { className: "font-semibold text-sm uppercase tracking-wider text-slate-300" }, "Entry Summary"),
        h("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" },
          h("div", null, h("p", { className: "text-slate-400 text-xs" }, "Type"), h("p", { className: "font-medium" }, form.entry_type)),
          h("div", null, h("p", { className: "text-slate-400 text-xs" }, "Fund Source"), h("p", { className: "font-medium" }, form.source_of_fund)),
          h("div", null, h("p", { className: "text-slate-400 text-xs" }, "Items"), h("p", { className: "font-medium" }, form.items.length)),
          h("div", null, h("p", { className: "text-slate-400 text-xs" }, "Total Value"), h("p", { className: "font-bold text-lg text-emerald-400" }, "\u20B9" + grandTotal.toLocaleString("en-IN")))
        ),
        form.vendor_name && h("p", { className: "text-xs text-slate-400" }, "Vendor: ", h("span", { className: "text-white" }, form.vendor_name)),
        form.purchase_order_no && h("p", { className: "text-xs text-slate-400" }, "PO: ", h("span", { className: "text-white" }, form.purchase_order_no))
      )
    );
  };

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4];
  const StepIcon = STEPS[step].icon;

  return h("div", { className: "max-w-5xl mx-auto space-y-6", "data-testid": "grn-form-page" },
    h("div", { className: "flex items-center gap-4" },
      h(Button, { variant: "outline", size: "sm", onClick: () => navigate("/grn"), "data-testid": "back-btn" }, h(ChevronLeft, { className: "w-4 h-4 mr-1" }), " Back"),
      h("div", null,
        h("h1", { className: "text-xl font-bold text-slate-900" }, isEdit ? "Edit GRN Entry" : "New GRN Entry"),
        h("p", { className: "text-xs text-slate-500" }, "Goods Received Note \u2014 " + (form.entry_type === "ASSET" ? "Asset" : "Consumable"))
      )
    ),
    renderStepper(),
    h(Card, { className: "border-slate-200" },
      h(CardHeader, { className: "border-b border-slate-100 bg-slate-50/50 pb-4" },
        h("div", { className: "flex items-center gap-3" },
          h("div", { className: "p-2 rounded-lg bg-slate-900 text-white" }, h(StepIcon, { className: "w-4 h-4" })),
          h("div", null,
            h(CardTitle, { className: "text-base" }, STEPS[step].title),
            h("p", { className: "text-xs text-slate-500 mt-0.5" }, STEPS[step].desc)
          )
        )
      ),
      h(CardContent, { className: "p-6" }, stepRenderers[step]())
    ),
    // Validation summary on the final step
    (() => {
      const issues = [];
      if (!form.date_of_receipt) issues.push("Pick the Date of Receipt on Step 2.");
      if (!form.items.length || !form.items[0].description) issues.push("Add at least one Item with a description on Step 3.");
      if (form.grn_source === "PO_BASED" && !form.po_reference_id) issues.push("Select a Purchase Order on Step 1 (PO-Based GRN).");
      if (!form.source_of_fund) issues.push("Select Source of Fund on Step 1.");
      if (grandTotal <= 0) issues.push("Total value must be greater than zero \u2014 fill price components on at least one item.");
      const isFinal = step === STEPS.length - 1;
      return h("div", { className: "space-y-3" },
        isFinal && issues.length > 0 && h("div", { className: "p-4 rounded-xl border border-amber-300 bg-amber-50", "data-testid": "grn-validation-summary" },
          h("p", { className: "text-sm font-semibold text-amber-900 mb-2" }, "\u26A0\uFE0F Please fix the following before saving (", issues.length, "):"),
          h("ul", { className: "list-disc ml-5 space-y-1" }, ...issues.map((m, k) => h("li", { key: k, className: "text-sm text-amber-800" }, m)))
        ),
        h("div", { className: "flex items-center justify-between" },
          h(Button, { variant: "outline", onClick: () => step > 0 ? setStep(step - 1) : navigate("/grn"), "data-testid": "prev-btn" },
            h(ChevronLeft, { className: "w-4 h-4 mr-1" }), step === 0 ? "Cancel" : "Previous"
          ),
          h("div", { className: "flex gap-2" },
            step < STEPS.length - 1
              ? h(Button, { onClick: () => setStep(step + 1), disabled: !canNext(), className: "bg-slate-900 hover:bg-slate-800", "data-testid": "next-btn" }, "Next ", h(ChevronRight, { className: "w-4 h-4 ml-1" }))
              : h(Button, { onClick: handleSubmit, disabled: submitting || issues.length > 0, className: "bg-emerald-600 hover:bg-emerald-700", "data-testid": "submit-grn-btn" }, h(Save, { className: "w-4 h-4 mr-2" }), submitting ? "Submitting..." : (issues.length > 0 ? `Fix ${issues.length} issue${issues.length > 1 ? "s" : ""} to submit` : (isEdit ? "Update GRN Entry" : "Submit GRN Entry")))
          )
        )
      );
    })()
  );
}

export default GRNForm;
